import Notifications from "vue-notification";
import VModal from "vue-js-modal";
import Loading from "vue-loading-overlay";
import Vuex from "vuex";
import VueFilterDateFormat from "@vuejs-community/vue-filter-date-format";
import VueProgressBar from "vue-progressbar";
import { VClosePopover, VPopover, VTooltip } from "v-tooltip";
import EVENT_BUS, { EVENT_BUS_TYPES } from "./eventBus";
// filters
import filters from "./store/global/_filters";

const defaults = {
  VueProgressBarColor: "#278FC9",
  VueLoadingOverlayColor: "#278FC9",
  EventBusTypes: {},
};

/**
 * Preparing single Vue instance
 * @param Vue
 * @param {Options} options
 * @return {*}
 */
export default function prepareVue(Vue, options = {}) {
  const opts = { ...defaults, ...options };

  Vue.config.devtools = process.env.NODE_ENV !== "production";
  Vue.config.silent = process.env.NODE_ENV === "production";
  Vue.config.performance = process.env.NODE_ENV !== "production";

  Vue.directive("cms-tooltip", VTooltip);
  Vue.directive("cms-close-popover", VClosePopover);
  Vue.component("CmsPopover", VPopover);

  Vue.config.ignoredElements = [...Vue.config.ignoredElements, "alert", "style"];

  Vue.use(Vuex);
  Vue.use(Notifications);

  Vue.use(VModal, {
    componentName: "CmsVueJsModal",
  });

  Vue.use(VueProgressBar, {
    color: opts.VueProgressBarColor,
    failedColor: "red",
    thickness: "4px",
    transition: {
      speed: "0.4s",
      opacity: "0.6s",
      termination: 300,
    },
  });
  Vue.use(VueFilterDateFormat);
  Vue.use(Loading, {
    // props
    color: opts.VueLoadingOverlayColor,
  });

  Vue.component("HtmlFragment", {
    functional: true,
    props: {
      html: {
        type: String,
        required: true,
      },
    },
    render(h, ctx) {
      return new Vue({
        beforeCreate() {
          this.$createElement = h;
        }, // not necessary, but cleaner imho
        template: `<div>${ctx.props.html}</div>`,
      }).$mount()._vnode.children;
    },
  });

  for (const [name, fnc] of Object.entries(filters)) {
    Vue.filter(name, fnc);
  }

  Object.defineProperties(Vue.prototype, {
    /**
     * Global Event bus
     */
    $eventBus: {
      enumerable: false,
      configurable: false,
      writable: false,
      value: {
        /**
         * @param {string} event
         * @param {Function} callback
         * @returns {Vue | any}
         */
        on: (event, callback) => EVENT_BUS.$on(event, callback),
        /**
         * @param {string} event
         * @param {Function} callback
         * @returns {Vue | any}
         */
        once: (event, callback) => EVENT_BUS.$once(event, callback),
        /**
         * @param {string} event
         * @param {Function} callback
         * @returns {Vue | any}
         */
        emit: (event, callback) => EVENT_BUS.$emit(event, callback),
        /**
         * @param {string} event
         * @param {Function} callback
         * @returns {Vue | any}
         */
        off: (event, callback) => EVENT_BUS.$off(event, callback),

        // TODO check is not exist duplicate event type
        type: { ...EVENT_BUS_TYPES.default, ...opts.EventBusTypes },
      },
    },
  });

  // Source: https://forum.vuejs.org/t/call-the-overrided-method-from-extend-mixin-think-super/1469
  // eslint-disable-next-line no-shadow, func-names
  Vue.prototype.$super = function (options) {
    return new Proxy(options, {
      // eslint-disable-next-line no-shadow, consistent-return
      get: (options, name) => {
        if (options.methods && name in options.methods) {
          return options.methods[name].bind(this);
        }
      },
    });
  };

  window.CmsVue = Vue;
  return Vue;
}

/**
 * @typedef {Object} Options
 * @property {Array} [EventBusTypes]
 * @property {String} [VueLoadingOverlayColor] - HEX color
 * @property {String} [VueProgressBarColor] - HEX color
 */
